import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import { Modal, Box, IconButton } from '@mui/material';
import { SignUp, SignIn } from '@clerk/clerk-react';
import CloseIcon from '@mui/icons-material/Close';
import { closeAuthModal, selectAuthModalState, selectDestination, selectUserIsAuthed, selectAuthUnavailable } from './authSlice';
import { clearAuthReturnPath, getAuthReturnPath } from './authReturnPath';

const GroupardyAuthModal = () => {
  const dispatch = useDispatch();
  const location = useLocation();
  const navigate = useNavigate();
  const { modalOpen, modalMode } = useSelector(selectAuthModalState);
  const destination = useSelector(selectDestination);
  const userIsAuthed = useSelector(selectUserIsAuthed);
  const authUnavailable = useSelector(selectAuthUnavailable);
  const currentPath = `${location.pathname}${location.search}`;
  const isAuthBouncePath = location.pathname === '/' || location.pathname === '/auth' || location.pathname === '/login' || location.pathname === '/signup';
  const savedReturnPath = getAuthReturnPath();
  const redirectUrl = isAuthBouncePath ? (destination || savedReturnPath || '/manage') : currentPath;

  useEffect(() => {
    if (!userIsAuthed) {
      return;
    }
    if (modalOpen) {
      dispatch(closeAuthModal());
    }
    // Don't rely solely on Clerk's forceRedirectUrl to bring the user back —
    // the email-verification signup flow and Clerk's hard window.location
    // fallback can drop the user on the homepage instead, abandoning an
    // in-progress create flow. The return path persists in localStorage across
    // that hard redirect, so once we detect an authed user we route them back
    // ourselves.
    //
    // Consume the saved path even when no navigation is needed (we're already
    // on it). currentPath is a dependency of this effect, so a path left behind
    // would fire again on the user's next route change and yank them back out
    // of wherever they went — e.g. out of the editor they just created.
    const returnPath = getAuthReturnPath();
    if (returnPath) {
      clearAuthReturnPath();
      if (returnPath !== currentPath) {
        navigate(returnPath, { replace: true });
      }
    }
  }, [dispatch, navigate, modalOpen, userIsAuthed, currentPath]);

  const handleClose = () => {
    localStorage.removeItem('groupardy.aiCreatePending');
    localStorage.removeItem('groupardy.manualCreatePending');
    clearAuthReturnPath();
    dispatch(closeAuthModal());
  };

  const handleBackgroundClick = (e) => {
    if (e.target === e.currentTarget) {
      handleClose();
    }
  };

  return (
    <Modal
      open={modalOpen}
      onClose={handleClose}
      aria-labelledby="auth-modal-title"
      sx={{
        backdropFilter: 'blur(4px)',
      }}
    >
      <>
        <IconButton
          onClick={handleClose}
          sx={{
            position: 'fixed',
            right: 16,
            top: 16,
            color: 'white',
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            '&:hover': {
              backgroundColor: 'rgba(0, 0, 0, 0.7)',
            },
            zIndex: 8,
          }}
        >
          <CloseIcon />
        </IconButton>
        
        <Box 
          onClick={handleBackgroundClick}
          sx={{ 
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: 'rgba(0, 0, 0, 0.0)',
          }}
        >
          <Box 
            onClick={(e) => e.stopPropagation()}
            sx={{
              position: 'relative',
            }}
          >
            {authUnavailable ? (
              <Box
                sx={{
                  width: '100%',
                  maxWidth: '440px',
                  borderRadius: '16px',
                  backgroundColor: '#16213e',
                  color: '#fff',
                  textAlign: 'center',
                  padding: '32px 28px',
                  fontFamily: "'Helvetica Neue', Helvetica, Arial, sans-serif",
                }}
              >
                <Box
                  component="h2"
                  sx={{
                    fontFamily: "'Helvetica Inserat', Impact, 'Arial Black', sans-serif",
                    textTransform: 'uppercase',
                    letterSpacing: '0.04em',
                    color: '#ffe066',
                    fontSize: '1.4rem',
                    margin: '0 0 12px',
                  }}
                >
                  Sign-in is unavailable
                </Box>
                <Box sx={{ color: 'rgba(255,255,255,0.78)', lineHeight: 1.6 }}>
                  We couldn't reach the sign-in service. This can happen on
                  preview links, or if your browser is blocking third-party
                  storage. You can still play and create games without signing in.
                </Box>
              </Box>
            ) : modalMode === 'signup' ? (
              <SignUp
                forceRedirectUrl={redirectUrl}
                fallbackRedirectUrl={redirectUrl}
                signInUrl="/login"
                appearance={{
                  elements: {
                    rootBox: {
                      width: '100%',
                      maxWidth: '500px',
                    },
                    card: {
                      boxShadow: 'none',
                    },
                  },
                }}
              />
            ) : (
              <SignIn
                forceRedirectUrl={redirectUrl}
                fallbackRedirectUrl={redirectUrl}
                signUpUrl="/signup"
                withSignUp
                appearance={{
                  elements: {
                    rootBox: {
                      width: '100%',
                      maxWidth: '500px',
                    },
                    card: {
                      boxShadow: 'none',
                    },
                  },
                }}
              />
            )}
          </Box>
        </Box>
      </>
    </Modal>
  );
};

export default GroupardyAuthModal; 
